// D6-1 / D6-2 / D6-5 — THE LANDING VIEW AND THE DATE MACHINE.
//
// **Opening the dashboard shows THE BUSINESS, never a member list.** Her order, and the shape of
// this file is that sentence: today's numbers, the top three rooms, the health strip, and one range
// control governing all of it.
//
// THREE RULES THIS COMPONENT KEEPS:
//   1. **EVERY NUMBER IS A DOOR.** Each tile names the tab it opens (`to`, in src/commandCenter.js),
//      and a tile without one cannot exist — the roster would fail its own fixture.
//   2. **THE RANGE IS ONE PIECE OF STATE**, lifted to the dashboard and passed in, so a tile and the
//      table on the next tab cannot disagree about what "30 days" meant.
//   3. **IT SAYS HOW OLD IT IS.** Today polls and says "live"; a ranged view is a snapshot and says
//      the time it was taken. Her honest-gap law, applied to the instruments she runs on.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote, postOwnerAction, ownerActionNote } from "../lib/owner-load";
import { ArrowUpRight, RefreshCw } from "lucide-react";
import {
  TODAY_TILES, HEALTH_TILES, MONEY_TILES, ESTIMATE_NOTE, RANGES,
  TODAY_POLL_MS, freshnessLabel, isLiveView, moneyLabel, morningStrip, morningNote,
  needsYouLines, NEEDS_YOU_CLEAR, NEEDS_YOU_UNREAD, ADVANCED_TITLES, advancedCaption,
  conversionLine,
  // ITEM 8 — what a health tile tells her before she taps it, composed by the law rather than in the
  // markup, so a gate RUNS the sentence instead of asking a regex whether two strings sit together.
  healthTileLabel,
} from "../commandCenter.js";
// ITEM 15 — her morning health line. The sentences live beside the roster the SERVER measures
// against, so a check's name has one answer on both sides of the wire (R28/R29).
import { patrolLine, patrolRows } from "../healthPatrol.js";
import { deliveryStrip, deliveryNote, deliveryCause } from "../deliveryVerdict.js";
// M1 — the ONE declaration of where an alert opens, and her clause 2's clear label/state, taken
// from the same law the alerts view reads so the two screens cannot word or judge it differently.
import { alertDoor, doorQuery } from "../alertDoors.js";
import { ALERT_OPEN, ALERT_CLEAR_LABEL, alertRows } from "../standingAlerts.js";
import { morningAuditNote, morningAuditState, morningGate } from "../morningAudit.js";

// DL-3.3: the shape `morningHealth` answers in. Declared here beside the one component that renders
// it, per this house's rule that a type moves with what draws it.
export type MorningHealth = {
  day: string; total: number;
  byStatus: Record<string, number>;
  byReason: Record<string, number>;
};

// DT-1c: the shape `deliveryHealth` answers in, declared beside the one component that renders it.
// The two gap lists are two fields because they are two defects (migration 075's own reason).
export type BeaconRow = {
  tag: string; userId: string | number | null; email: string; kind: string;
  sentAt: string | null; minutesSilent: number; devices: number | null;
};
export type DeliveryHealth = {
  window: number; graceMinutes: number;
  sent: number; received: number; displayed: number;
  undelivered: Array<BeaconRow & { cause: string }>;
  drewNothing: BeaconRow[];
};

// DT-3a: the shape `morningAudit` answers in — one morning reconciled against the beacons for its
// own sends, which is the join neither strip above could make.
export type MorningAuditRow = {
  userId: string | null; who: string; tag: string; reason: string; cause: string;
  minutes: number | null; state: string; diagnostic?: boolean; heals?: boolean;
};
export type MorningAuditReport = {
  day: string; graceMinutes: number; members: number; sent: number; delivered: number;
  counts: Record<string, number>;
  gaps: MorningAuditRow[];
  rows: MorningAuditRow[];
  state: string;
};

// ITEM 10: `number | null` because the Stripe-fed cells (revenue, net, paid customers) come back null
// when the read failed, and a type that says otherwise is a type that hides R18 from the renderer.
type Tiles = Record<string, number | null> & { health: Record<string, number> };
type Delta = { pct: number | null; direction: string; text: string };
type Landing = {
  range: { key: string; label: string; days: number };
  tiles: Tiles;
  deltas: Record<string, Delta> | null;
  rooms: Array<{ room: string; visits: number; generations: number }>;
  takenMs: number;
  testMode: boolean;
  mornings: MorningHealth | null;
  delivery: DeliveryHealth | null;
  morningAudit: MorningAuditReport | null;
  // INSTALL-1 clause 5. `null` is a read that could not be made and is a DIFFERENT fact from zero
  // desktop members (R18/R65) — only one of the two is about her audience.
  desktopOnlyPush: number | null;
  // J1 (her I4-Q1 YES): the vendor meter's one line; "" or null draws nothing.
  vendorMeter?: { line: string } | null;
  // ITEM A — her actionable list. `null` is a payload that could not be read, and it is a DIFFERENT
  // fact from three zeros (R18) — see `NeedsYou` below, where the distinction is the component.
  // DESK-LAW-1 — `desk` is everyone waiting for a real answer, `deskLate` is the subset this house
  // has already broken its own 48-hour promise to. Two fields because they are two mornings (R18).
  needsYou: { held: number; tickets: number; money: number; desk: number; deskLate: number } | null;
  // ITEM 15 — the nightly patrol's answer. `null` is a read that failed; `{ ranAt: null }` is a
  // patrol that has NEVER RUN, which is a third fact and the one this whole item exists to surface.
  patrol: { ranAt: string | null; dayKey: string; reds: number; unknowns: number;
    checks: Array<{ key: string; state: string; note: string }> } | null;
  // M1 — the OPEN standing alerts, so her clause 2's clear can be offered where she is already
  // standing. A null is a REFUSED READ and is a different fact from an empty list (R18).
  standingAlerts: Array<{ id: string; class: string; count: number; members: number }> | null;
  // N2 — the /talk sentry's last-green. `null` is a read that FAILED; `state: "silent"` is a sentry
  // that is not reporting, which is a THIRD fact and the one this house has actually suffered.
  sentry: { state: string; say: string; stale: boolean; minutesSinceGreen: number | null;
    minutesSincePass: number | null; readable: boolean } | null;
  // T4 (c) — the Truth watchman's last-green, the same three-state reading (`null` = a failed read).
  marketSentry?: { state: string; say: string; stale: boolean } | null;
};

/**
 * ITEM 15 — **HER MORNING HEALTH LINE. ONE SENTENCE, IN PLAIN ENGLISH, AND WHAT IS UNDER IT IS THE
 * SAME SENTENCE PER CHECK.**
 *
 * Nothing on this screen composes a sentence from a machine key: every word comes from
 * `src/healthPatrol.js`, which the SERVER also reads, so what a check is called has one answer
 * (R28/R29) and ITEM A's plain-language law is satisfied at the declaration rather than at the paint.
 *
 * **FOUR STATES, AND THE TWO IN THE MIDDLE ARE THE ROUND (R18/R65).** `undefined` holds still while
 * the payload is in flight; `null` is a read that FAILED and says so; `ranAt: null` is a patrol that
 * has NEVER RUN — *"the job did not run"* and *"the job found nothing wrong"* are different mornings
 * and DL-1 established that distinction on this page already; and an answer is an answer. A build
 * that collapsed the third into the fourth would print "nothing wrong" over a watchman that is not
 * watching, which is the exact silence her item was written to end.
 *
 * Hookless on purpose, so a gate can CALL it.
 */
export function PatrolLine({
  patrol, onGo,
}: {
  patrol?: Landing["patrol"];
  // M1 — a door is a VIEW **AND** A FILTER, so the destination travels with the selection that
  // makes it open onto the exact items counted. Optional, so every existing caller that has nothing
  // to select is byte-identical to the one that shipped.
  onGo: (view: string, query?: string) => void;
}) {
  if (patrol === undefined) {
    return <section className="owner-patrol owner-patrol--waiting" aria-busy="true"><p className="owner-patrol__line">…</p></section>;
  }
  const line = patrolLine(patrol);
  const rows = patrolRows(patrol) || [];
  const bad = rows.filter((r) => r.state !== "ok");
  return (
    <section className="owner-patrol" aria-label="Last night's check">
      <p className={`owner-patrol__line${patrol === null || !patrol.ranAt ? " owner-fresh--failed" : ""}`} role="status">{line}</p>
      {/* Only what is wrong is listed. A full pass-list on the Overview is the "deep view" her ITEM A
          order moved to Advanced — and every line here is a door, because a number she can see is one
          she can act on. */}
      {bad.length ? (
        <ul className="owner-patrol__list">
          {bad.map((r) => (
            <li key={r.key}>
              {/* >>> M1 — **A ROW IS A DOOR ONLY IF IT HAS ONE, AND THE LAW DECIDES, NOT THIS
                  MARKUP.** <<< Every one of these was a button onto `PATROL_TO`, one shared
                  destination for all six checks — so *"Outside services"* opened the IT ticket list,
                  which by that check's own `files: false` ruling can never contain a row about it.
                  `opensAsDoor` is `alertRendersAsDoor`'s answer, computed where a gate can RUN it.
                  A row without one is drawn as the READING it is: same words, same state colour, no
                  promise that tapping it leads anywhere (DECISIONS M1-3 — what her clause 4 removes
                  is the door, never the measurement). */}
              {r.opensAsDoor ? (
                <button
                  type="button"
                  className={`owner-patrol__item is-${r.state}`}
                  onClick={() => onGo(r.door, r.doorQuery)}
                >
                  <strong>{r.say}</strong>
                  <span>{r.note}</span>
                  <ArrowUpRight size={14} />
                </button>
              ) : (
                <div className={`owner-patrol__item owner-patrol__item--flat is-${r.state}`}>
                  <strong>{r.say}</strong>
                  <span>{r.note}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * N2 — **THE /talk SENTRY'S LAST-GREEN LINE.** Her standing exception, 2026-09-02: *the door that
 * just burned $9K does not go back under traffic unwatched... last-green on the dashboard.*
 *
 * **THE SENTENCE IS THE LAW'S, NEVER THIS FILE'S.** `talkSentryLastGreen` composes it and the
 * SERVER reads the same function for the alarm, so what the tile says and what wakes her phone
 * cannot disagree (R28/R29) — and it is composed there rather than here because `src/talkSentry.js`
 * imports nothing, which is what lets a gate run every state of it.
 *
 * **FOUR STATES, AND THE MIDDLE TWO ARE THE POINT (R18/R65).** `undefined` holds still while the
 * payload is in flight; `null` is a read that FAILED; `silent` is a sentry that is not reporting;
 * and green is green. A build that drew silent as green would print *the door walked clean* over a
 * watchman that has not walked anything since Tuesday, which is the exact silence this item ends.
 *
 * Hookless on purpose, so a gate can CALL it.
 */
export function SentryLine({ sentry }: { sentry?: Landing["sentry"] }) {
  if (sentry === undefined) {
    return <section className="owner-patrol owner-patrol--waiting" aria-busy="true"><p className="owner-patrol__line">…</p></section>;
  }
  if (sentry === null) {
    return (
      <section className="owner-patrol" aria-label="The Ask Lala funnel sentry">
        <p className="owner-patrol__line owner-fresh--failed" role="status">The /talk sentry could not be read just now.</p>
      </section>
    );
  }
  // A sentry that is silent or has failed wears the same failed mark the patrol's own never-ran line
  // wears, because both say the same thing to her eye: this reading is not a clean bill of health.
  const wrong = sentry.state !== "green";
  return (
    <section className="owner-patrol" aria-label="The Ask Lala funnel sentry">
      <p className={`owner-patrol__line${wrong ? " owner-fresh--failed" : ""}`} role="status">{sentry.say}</p>
    </section>
  );
}

/**
 * T4 (c), her order of 2026-09-30 — **THE TRUTH WATCHMAN'S LAST-GREEN LINE**, beside the /talk
 * sentry's and drawn the same way: the sentence is the server law's (`marketSentryLastGreen`), and
 * anything but green wears the failed mark. Hookless, so a gate can call it.
 */
export function MarketSentryLine({ sentry: watch }: { sentry?: Landing["marketSentry"] }) {
  if (watch === undefined) return null;
  if (watch === null) {
    return (
      <section className="owner-patrol" aria-label="The Truth watchman">
        <p className="owner-patrol__line owner-fresh--failed" role="status">The Truth watchman could not be read just now.</p>
      </section>
    );
  }
  const wrong = watch.state !== "green";
  return (
    <section className="owner-patrol" aria-label="The Truth watchman">
      <p className={`owner-patrol__line${wrong ? " owner-fresh--failed" : ""}`} role="status">{watch.say}</p>
    </section>
  );
}

/**
 * ITEM A — **WHAT NEEDS HER, AND EVERY LINE OPENS WHERE SHE ACTS ON IT.**
 *
 * Her Overview clause names three: held items awaiting review, tickets open, money issues in her
 * pile — *every line CLICKING THROUGH to where she acts on it*. So each line is a button and its
 * destination is a VIEW off the roster, never a literal typed here (R20's shape: eighteen call sites
 * typing a room name is how a destination stops existing without anything going red).
 *
 * **THREE STATES, NOT TWO (R18), AND THE MIDDLE ONE IS WHY THIS IS A COMPONENT AND NOT A `.map()`.**
 * `undefined` is a payload that HAS NOT ARRIVED and it holds still. `null` is a payload that arrived
 * and could not be READ, and it says so. An object with three zeros is an ANSWER and earns
 * *"Nothing needs you right now."* **Collapsing the first two puts that sentence on the screen of an
 * owner whose dashboard could not reach the database** — the one wrong thing to tell her, and R65's
 * finding on this exact page in another spelling.
 *
 * Hookless on purpose, so a gate can CALL it.
 */
export function NeedsYou({
  counts, onGo,
}: {
  counts?: { held: number; tickets: number; money: number; desk: number; deskLate: number } | null;
  // M1 — a door is a VIEW **AND** A FILTER, so the destination travels with the selection that
  // makes it open onto the exact items counted. Optional, so every existing caller that has nothing
  // to select is byte-identical to the one that shipped.
  onGo: (view: string, query?: string) => void;
}) {
  if (counts === undefined) {
    return <section className="owner-needs owner-needs--waiting" aria-busy="true"><p className="owner-needs__line">…</p></section>;
  }
  if (counts === null) {
    return (
      <section className="owner-needs owner-needs--failed">
        <p className="owner-needs__line owner-fresh--failed" role="status">{NEEDS_YOU_UNREAD}</p>
      </section>
    );
  }
  const lines = needsYouLines(counts) || [];
  if (!lines.length) {
    return <section className="owner-needs owner-needs--clear"><p className="owner-needs__line">{NEEDS_YOU_CLEAR}</p></section>;
  }
  return (
    <section className="owner-needs" aria-label="What needs you">
      <ul className="owner-needs__list">
        {lines.map((l) => (
          <li key={l.key}>
            {/* M1's rendered proof: the line is identified by its KEY rather than by its words or
                its position (R41). ITEM A's own lesson cost five walkthroughs four days of measuring
                nothing, because they were written against labels she then reworded. */}
            <button type="button" className="owner-needs__item" data-needs-key={l.key} data-needs-to={l.to}
              onClick={() => onGo(l.to, l.query)}>
              <strong className="owner-needs__n">{l.n.toLocaleString("en-US")}</strong>
              <span className="owner-needs__text">{l.text}</span>
              <ArrowUpRight size={15} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * **THE LANDING READ, DECLARED ONCE, BECAUSE TWO VIEWS ASK FOR IT (R28/R29).**
 *
 * ITEM A moved the three beacon screens off the Overview and onto their own view under Advanced, and
 * both draw from `/api/owner/command/landing`. The first draft wrote the loader a SECOND time inside
 * `OwnerDelivery` — line for line, including R65's read-the-answer guard — and **R68's shared-literal
 * ceiling is what caught it**, in the round that wrote it: an anchor that used to name exactly one
 * place in this file suddenly named two.
 *
 * That is not a tidy-up. R65's finding on this exact page was a refused load leaving `data` at null
 * with NOTHING SAID, so the tab drew itself out of `data?.x || []` and she read a business number
 * that never arrived. Two copies of that guard is two places for it to be got wrong, and the second
 * copy is always the one somebody writes from memory.
 */
function useLanding(rangeQuery: string, onDenied: () => void) {
  const [data, setData] = useState<Landing | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [note, setNote] = useState("");

  // R65: the answer is READ. The paint is guarded by POSITION (R52) and the note lands on the
  // freshness line, which is the one line whose whole job is saying how current these numbers are.
  const load = useCallback(() => {
    setLoading(true);
    loadOwnerData<Landing>(`/api/owner/command/landing?${rangeQuery}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) { setData(answer.data); setNow(Date.now()); }
        setNote(ownerLoadNote(answer));
      })
      .finally(() => setLoading(false));
  }, [rangeQuery, onDenied]);

  return { data, loading, note, now, setNow, load };
}

export function OwnerOverview({
  rangeQuery, rangeKey, onDenied, onGo,
}: {
  rangeQuery: string; rangeKey: string;
  onDenied: () => void; onGo: (tab: string, query?: string) => void;
}) {
  const { data, loading, note, now, setNow, load } = useLanding(rangeQuery, onDenied);
  useEffect(() => { load(); }, [load]);

  // D6-5: TODAY REFRESHES ITSELF. A ranged view does not — it is a snapshot of a period that is not
  // moving, and polling it would be motion for its own sake.
  useEffect(() => {
    if (!isLiveView(rangeKey)) return;
    const poll = window.setInterval(load, TODAY_POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => { window.clearInterval(poll); window.clearInterval(tick); };
  }, [rangeKey, load]);

  // >>> M1 — HER CLAUSE 2, AND THE THREE THINGS IT NEEDS. <<<
  //
  // `clearing` is a KEY rather than a boolean, so two tiles cannot disable each other; the post is
  // the alerts view's own door; and the payload is re-read afterwards, because a screen still
  // offering a clear she just took is the same lie one row down.
  const [clearing, setClearing] = useState("");
  const [clearNote, setClearNote] = useState("");
  // **IT ASKS `alertRows`, THE ONE MAPPER, RATHER THAN READING THE RAW ROW** (R28/R29; that
  // function's own comment states the rule). Reading `.class` and a state off the payload here
  // would be a second answer to *is this alert open*, on the two screens that must never disagree
  // about whether she has already dealt with something.
  //
  // **`null` IS A REFUSED READ AND MUST NOT LOOK LIKE AN EMPTY LIST** (R18). Read as empty, every
  // clear control simply would not draw, which is indistinguishable from *all acknowledged already*.
  const openAlerts = alertRows(data?.standingAlerts);
  const openAlertFor = (key: string) => {
    if (openAlerts === null) return null;
    return openAlerts.find((a) => a.key === key && a.state === ALERT_OPEN) || null;
  };
  const clearFromOverview = async (key: string) => {
    const row = openAlertFor(key);
    if (!row) return;
    setClearing(key);
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/alert-clear", { id: row.id });
    if (!answer.ok && answer.kind === "denied") { onDenied(); setClearing(""); return; }
    setClearNote(ownerActionNote(answer));
    setClearing("");
    await load();
  };
  // The door's own selection, so a tile opens on the exact rows it counted rather than on the whole
  // surface behind it. `doorQuery` answers "" for a door with no filter, which is byte-for-byte the
  // navigation that tile has always had.
  const doorQueryFor = (key: string) => {
    const door = alertDoor(key);
    return door ? doorQuery(door) : "";
  };

  const value = (key: string) => {
    const raw = Number(data?.tiles?.[key] ?? 0);
    // **A MONEY NUMBER THAT COULD NOT BE READ IS NOT A ZERO** (her order, 2026-08-08). Revenue and net
    // come back `null` when the Stripe read failed, and rendering that as $0.00 would tell an owner
    // she took nothing today because an API call timed out — indistinguishable from a quiet day.
    //
    // **ITEM 10 WIDENED IT TO EVERY KIND OF NUMBER, BECAUSE PAID CUSTOMERS IS A COUNT OFF THE SAME
    // READ.** The guard asked `MONEY_TILES.has(key)` FIRST, so a null COUNT fell straight through to
    // `Number(null ?? 0)` and rendered a confident **0** — *"nobody paid today"* and *"we could not
    // ask Stripe"* are different facts (R18), and on this tile the first one is the one she would
    // act on.
    if (data && (data.tiles?.[key] === null || data.tiles?.[key] === undefined)) return "—";
    return MONEY_TILES.has(key) ? moneyLabel(raw, { testMode: Boolean(data?.testMode) }) : raw.toLocaleString("en-US");
  };

  return (
    <div className="owner-overview">
      <div className="owner-overview__bar">
        <p className="owner-overview__fresh">
          {/* R65: the note SUPERSEDES the freshness label, and on THIS tab it also supersedes the
              word "live" — D6 ruled that only Today says live, and a live claim over a poll that just
              failed is the one dishonesty that ruling exists to forbid. */}
          {note ? <span className="owner-fresh--failed" role="status">{note}</span>
            : data
            ? isLiveView(rangeKey)
              ? <><span className="is-live" /> live, {data.range.label.toLowerCase()}</>
              : <>Taken {freshnessLabel(data.takenMs, now)} · {data.range.label}</>
            : "..."}
        </p>
        <button type="button" className="owner-link" onClick={load} disabled={loading}>
          <RefreshCw size={14} />{loading ? "Reading..." : "Refresh"}
        </button>
      </div>
      {data?.vendorMeter?.line ? <p className="owner-overview__meter">{data.vendorMeter.line}</p> : null}

      {/* >>> **ITEM 6b, HER ORDER OF 2026-10-01: "When I sign into the dashboard... I get all of these
          messages at the top that I do not want."** <<< The four lines that sat here (what is waiting
          on her, last night's check, the /talk sentry, the Truth watchman) are MOVED, not deleted:
          they live in the closed fold at the foot of this page, and every count also lives in its own
          section (IT Tickets, Advanced > Needs you, Overview > Alerts). */}
      {/* THE BUSINESS. Every tile opens something. */}
      <div className="owner-tiles">
        {TODAY_TILES.map((t) => {
          const d = data?.deltas?.[t.key];
          return (
            <button key={t.key} type="button" className="owner-tile" onClick={() => onGo(t.to)}
              aria-label={`${t.label} — open ${t.to}`}>
              <span className="owner-tile__label">
                {t.label}
                {t.estimate ? <em className="owner-tile__est">{ESTIMATE_NOTE}</em> : null}
              </span>
              <strong className="owner-tile__value">{data ? value(t.key) : "—"}</strong>
              <span className="owner-tile__foot">
                {d?.text ? <em className={`owner-tile__delta is-${d.direction}`}>{d.text}</em> : <em />}
                <ArrowUpRight size={13} />
              </span>
            </button>
          );
        })}
      </div>

      {/* **ITEM 10 — THE CONVERSION LINE, RENDERED RIGHT ON OVERVIEW.** Her words: "X signups, Y
          paid, Z%". Directly under the tiles the two numbers come from, and composed by the SAME law
          the Financials tab uses (`conversionLine`), so the two screens cannot word it differently
          or divide differently (R28/R29). It holds still while the payload is in flight rather than
          rendering "0 signups, 0 paid, 0%" over nothing. */}
      {data ? (
        <p className="owner-overview__conversion">
          {conversionLine({ signups: data.tiles?.signups, paid: data.tiles?.paidCustomers }).text}
        </p>
      ) : null}

      {/* TOP THREE ROOMS BY THE PERIOD, with visits and generations each. */}
      <h3 className="owner-overview__h">Busiest rooms</h3>
      <ul className="owner-rooms">
        {(data?.rooms || []).map((r) => (
          <li key={r.room}>
            <button type="button" onClick={() => onGo("analytics")}>
              <strong>{r.room}</strong>
              <span>{r.visits.toLocaleString("en-US")} visits</span>
              <span>{r.generations.toLocaleString("en-US")} generations</span>
            </button>
          </li>
        ))}
        {data && !data.rooms.length ? <li className="owner-page__note">Nothing in this window.</li> : null}
      </ul>

      {/* THE HEALTH STRIP. */}
      <h3 className="owner-overview__h">Health</h3>
      {/* M1 — the SERVER's own sentence about what a clear did, never one composed here (R83). A
          clear that was refused says so rather than leaving a tile that looks handled. */}
      {clearNote ? <p className="owner-page__note owner-health__note" role="status">{clearNote}</p> : null}
      <div className="owner-health">
        {HEALTH_TILES.map((h) => (
          // ITEM 8 — **THE `dormant` CAPTION IS GONE AND THE TILE SAYS WHAT IT COUNTS.** It rendered
          // *"when push ships"* under a number measuring 45,567 pushed rows against 401 live
          // subscriptions — a claim on her screen that a shipped feature has not shipped, which is
          // her *"an alert she cannot act on does not render"* clause in its purest form.
          //
          // **THE `counts` SENTENCE RIDES THE LABEL RATHER THAN ADDING A LINE TO HER ROW**, which is
          // the conservative call (DECISIONS ITEM8-4): her clause names DOORS, not captions, so the
          // description reaches a screen reader and a hover and nothing on this strip moves. The
          // declaration itself is what `healthTileViolations` refuses a tile for lacking.
          <div key={h.key} className="owner-health__cell">
            <button type="button" className="owner-health__item" data-health-key={h.key}
              onClick={() => onGo(h.to, doorQueryFor(h.key))}
              aria-label={healthTileLabel(h)} title={h.counts}>
              <strong>{data ? Number(data.tiles.health?.[h.key] ?? 0).toLocaleString("en-US") : "—"}</strong>
              <span>{h.label}</span>
            </button>
            {/* >>> M1 / HER CLAUSE 2 — **CLEARABLE FROM THE OVERVIEW.** <<< Drawn only where an alert
                of this class is really OPEN, so it is never a control with nothing behind it — which
                is the disease this whole round is about, and it would be a poor round that cured it
                by adding a dead button one row down. It posts the SAME `/api/owner/alert-clear` the
                alerts view posts, so *has she seen this* keeps one answer (R28/R29), and the
                acknowledgement's own law is untouched: **cleared stays cleared until the problem
                grows past the numbers she saw.** */}
            {openAlertFor(h.key) ? (
              <button type="button" className="owner-health__clear" data-clear-key={h.key}
                disabled={clearing === h.key}
                onClick={() => void clearFromOverview(h.key)}>
                {ALERT_CLEAR_LABEL}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {/* ITEM 7c — HER OLD STACK IS NOT ON OVERVIEW AT ALL NOW, not even folded at the foot (her word,
          2026-10-01 afternoon). It lives under Advanced as "Waiting and last night's checks"
          (`OwnerChecks` below), the same four components on the same payload. */}

      {/* ITEM A — **THE THREE BEACON SCREENS ARE NOT HERE ANY MORE.** Her order names them by
          hand in the removed list — *the raw delivery-beacon morning screens* — and puts them under
          Advanced. NOTHING IS DELETED: `OwnerDelivery` below renders all three, off the same payload,
          from the Advanced menu. What this page keeps is the business and what needs her. */}
    </div>
  );
}

/**
 * ITEM 7c — **HER OLD STACK, UNDER ADVANCED.** Her order: none of these lines at the top of her
 * dashboard, and their information still reachable under Advanced, not deleted. So this is a MOVE:
 * the same four components (what is waiting, last night's check, the /talk sentry, the Truth
 * watchman), fed by the SAME landing read the Overview does, open rather than folded because here
 * she came looking for them. Every line is still the door it was.
 */
export function OwnerChecks({
  rangeQuery, onDenied, onGo,
}: {
  rangeQuery: string; onDenied: () => void; onGo: (tab: string, query?: string) => void;
}) {
  const { data, load } = useLanding(rangeQuery, onDenied);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="owner-checks" aria-label="Waiting and last night's checks">
      {/* ITEM A — **WHAT NEEDS HER, FIRST, BECAUSE IT IS THE ONLY THING ON THIS PAGE SHE HAS TO
          DO.** Above the tiles on purpose: the numbers are how the business is, the list is what is
          waiting, and an owner opening her dashboard to find out whether anything needs her should
          not have to scroll past seven figures to learn it. */}
      {/* THREE STATES: `undefined` while the payload is in flight, `null` when it arrived and could
          not be read, an object when it is an answer. `data ? … : null` would collapse the first two
          and put "Nothing needs you right now." on a dashboard that never loaded. */}
      <NeedsYou counts={data ? data.needsYou : undefined} onGo={onGo} />

      {/* ITEM 15 — **HER MORNING HEALTH LINE, DIRECTLY UNDER WHAT NEEDS HER AND ABOVE THE BUSINESS.**
          The list above is what people have left for her; this is what the house found while she was
          asleep. Both are things to DO, and both sit above seven figures she does not have to scroll
          past to learn whether anything is wrong. `undefined` while the payload is in flight, so the
          line holds still rather than claiming a clean night. */}
      <PatrolLine patrol={data ? data.patrol : undefined} onGo={onGo} />

      {/* N2 — **THE SENTRY, DIRECTLY UNDER THE NIGHT'S CHECK, BECAUSE IT IS THE SAME KIND OF FACT.**
          The patrol says what the house found while she was asleep; this says whether the door her
          advertising points at was walked, and when it last walked clean. `undefined` in flight, so
          it holds still rather than claiming a green door. */}
      <SentryLine sentry={data ? data.sentry : undefined} />
      <MarketSentryLine sentry={data ? data.marketSentry : undefined} />

    </section>
  );
}

/**
 * ITEM A — **THE THREE BEACON SCREENS, UNDER ADVANCED.**
 *
 * They were the bottom two thirds of the Overview and they are the clearest example of what her
 * order is about: `line-refused`, `worker-not-adopted`, DT-2 and a grace window in minutes are the
 * right words for the person diagnosing a silent morning, and they are the wrong words on the screen
 * an owner opens to find out whether anything needs her.
 *
 * **IT ASKS THE SAME ROUTE RATHER THAN A NEW ONE.** `landing` already carries `mornings`, `delivery`
 * and `morningAudit`, so a second endpoint would be a second answer to the same question (R28/R29) —
 * and the three strips themselves are the same exported components the Overview rendered, unchanged,
 * so this is a MOVE and not a rewrite.
 */
export function OwnerDelivery({ rangeQuery, onDenied }: { rangeQuery: string; onDenied: () => void }) {
  // The SAME read the Overview does — one declaration, so a refused load cannot be handled two ways.
  const { data, note, load } = useLanding(rangeQuery, onDenied);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="owner-deep">
      {/* R65: the answer is READ. Without this the three strips below draw themselves out of a
          payload that never arrived and every count reads as a real zero. */}
      {note ? <p className="owner-page__note owner-page__note--failed" role="alert">{note}</p> : null}
      <OwnerMorningStrip health={data ? data.mornings : null} />
      <OwnerDeliveryStrip health={data ? data.delivery : null} />
      {/* INSTALL-1 clause 5 — **the desktop-only permission count, REPORTED.** Beside the delivery
          strip, because it answers the same question one step earlier: who CAN be reached at all.
          Three states, never two (R18/R65) — a payload in flight holds still, a read that could not
          be made says so, and zero is an ANSWER. */}
      <OwnerDesktopPush count={data ? data.desktopOnlyPush : undefined} />
      <OwnerMorningAudit audit={data ? data.morningAudit : null} />
    </div>
  );
}

// DT-1c — **"SENT" IS NOT A SUCCESS STATE, AND THIS IS THE SCREEN THAT SAYS SO.** Her order's own
// clause: a send with no received-beacon inside an hour is UNDELIVERED, loudly, with the member and a
// probable cause named. DT-1 built the chain, DT-1b held its crossing, and until now nothing read it
// — `undelivered` and its siblings had zero call sites outside their own store (R38: a capability
// wired to nothing has no error state, and this one IS the error state).
//
// Hookless on purpose, so the gate CALLS it in every state — including the two that only ever appear
// when something is wrong, which is exactly the pair a browser check would never happen to be
// standing in front of.
//
// **THE THREE FACTS ARE DRAWN APART.** Sent, reached the device, drew on the screen — because a push
// that reaches the worker and fails to draw is a different defect from one that never arrives, and
// the three incidents this whole table was built for were indistinguishable precisely because
// something upstream collapsed them into one boolean.
//
// `health: null` is a payload that has not arrived (R18 / W5-2's third state) and it holds still.
// >>> INSTALL-1, HER CLAUSE 5 — **THE DESKTOP-ONLY PERMISSION COUNT, REPORTED.** <<<
//
// *How many members granted notifications on a DESKTOP and nowhere else.* It is the number that says
// whether clause 5 is worth anything: before this round a desktop browser was refused the ask with
// *"not the installed app"*, naming a thing that does not exist on her machine, so this count could
// only ever have been members who found the manual control in the account menu. **It is measured
// FORWARD from the deploy.**
//
// **THREE STATES, NEVER TWO (R18/R65), AND THE MIDDLE ONE IS WHY THIS IS A COMPONENT.**
//   `undefined` — the payload has not arrived. It HOLDS STILL, because a number that flashes zero
//                 for a frame on every dashboard open is a fact about the network wearing the face
//                 of a fact about her audience.
//   `null`      — the read ARRIVED and could not be made. It says so, in its own sentence.
//   a number    — an ANSWER, and **zero is one of them**: nobody has granted on a desktop yet is
//                 exactly what this round expects to read on the day it ships.
//
// Hookless, so a gate CALLS it in all three states.
export function OwnerDesktopPush({ count }: { count?: number | null }) {
  if (count === undefined) return null;
  return (
    <div className="owner-mornings owner-desktop-push">
      <h3 className="owner-overview__h">Notifications on a computer</h3>
      {count === null ? (
        <p className="owner-page__note owner-page__note--failed" role="alert">
          That count could not be read just now.
        </p>
      ) : (
        <div className="owner-health">
          <div className="owner-health__item">
            <strong>{count.toLocaleString("en-US")}</strong>
            <span>Only on a computer</span>
            <em>Members who turned notifications on at a computer and nowhere else.</em>
          </div>
        </div>
      )}
    </div>
  );
}

export function OwnerDeliveryStrip({ health }: { health: DeliveryHealth | null }) {
  if (!health) return null;
  const strip = deliveryStrip(health);
  const note = deliveryNote(strip);
  return (
    <div className="owner-mornings owner-delivery">
      <h3 className="owner-overview__h">{ADVANCED_TITLES.delivery}{strip.window ? ` — last ${strip.window}h` : ""}</h3>
      <div className="owner-health">
        <div className="owner-health__item"><strong>{strip.sent.toLocaleString("en-US")}</strong><span>Sent</span><em>{advancedCaption("delivery.sent")}</em></div>
        <div className="owner-health__item"><strong>{strip.received.toLocaleString("en-US")}</strong><span>Reached the phone</span><em>{advancedCaption("delivery.received")}</em></div>
        <div className="owner-health__item"><strong>{strip.displayed.toLocaleString("en-US")}</strong><span>Appeared on screen</span><em>{advancedCaption("delivery.displayed")}</em></div>
        <div className="owner-health__item"><strong>{strip.undelivered.toLocaleString("en-US")}</strong><span>Never arrived</span><em>{advancedCaption("delivery.undelivered")}</em></div>
      </div>

      {/* A TOTAL IS NOT A PATTERN (DL-3.3, one instrument over). Ranked, so the thing happening most
          is the thing she reads first, and every cause the window holds is printed. */}
      {strip.causes.length ? (
        <ul className="owner-mornings__reasons">
          {strip.causes.map((c) => (
            <li key={c.cause}><strong>{c.n.toLocaleString("en-US")}</strong> <span>{c.label}</span></li>
          ))}
        </ul>
      ) : null}

      {/* **AND A PATTERN IS NOT A PERSON.** Her order asks for the member BY NAME, so one line per
          member however many of her sends went quiet — a broken phone is one row rather than a wall
          that buries the other nine members behind it. The cause carries its own sentence and what
          it costs her, because three of the four heal themselves on the member's next open and one
          does not, and a strip that does not say which produces a to-do list nobody can work. */}
      {strip.members.length ? (
        <ul className="owner-delivery__members">
          {strip.members.map((m) => (
            <li key={m.who}>
              <strong>{m.who}</strong>
              <span>{m.n === 1 ? "1 send" : `${m.n} sends`}, quiet {m.minutes.toLocaleString("en-US")} min</span>
              {m.causes.map((key) => (
                <em key={key} className="owner-delivery__cause">
                  {deliveryCause(key).label} — {deliveryCause(key).why} {deliveryCause(key).act}
                </em>
              ))}
            </li>
          ))}
        </ul>
      ) : null}

      {/* **THE GRACE IS PRINTED, NEVER PROMISED.** It is the store's own number arriving in the
          payload; typing "an hour" here is how the screen comes to say one thing while the sweep
          does another. */}
      {strip.undelivered && strip.graceMinutes
        ? <p className="owner-mornings__note">Silent for more than {strip.graceMinutes} minutes.</p>
        : null}

      {/* Reached the worker and never drew — counted apart, and named apart, because it is the app on
          the device rather than the endpoint or the send. */}
      {strip.drewNothing
        ? <p className="owner-mornings__note" role="status">
            {strip.drewNothing.toLocaleString("en-US")} reached the device and never drew.
          </p>
        : null}

      {note ? <p className="owner-mornings__note" role="status">{note}</p> : null}
    </div>
  );
}

// DT-3a — **THE MORNING AND ITS BEACONS, INTRODUCED, AND THE GATE FOR THE ELITES READ OFF IT.**
//
// The two strips above answer two different questions over two different windows: what the JOB
// decided, and what the DEVICES answered. Nothing joined them, so this page could print "3 Sent" and
// "0 reached the device" with no instrument saying those were the same three sends and the morning
// had failed. Her order asks for "sends vs received-beacons vs displays, PER MEMBER, the gap list".
//
// **THE WORD IS EARNED HERE AND NOWHERE ELSE.** `Delivered` counts rows a beacon said drew; every
// other state is named for what it really is. That is the ban held as arithmetic (`isDeliveredState`),
// rather than as a rule somebody has to remember when writing a heading.
//
// Hookless on purpose, so the gate CALLS it in every state — including `quiet` and the two that only
// appear when something is wrong, which is exactly the set a browser check never happens to be
// standing in front of. `audit: null` is a payload that has not arrived (R18) and it holds still.
export function OwnerMorningAudit({ audit }: { audit: MorningAuditReport | null }) {
  if (!audit) return null;
  const note = morningAuditNote(audit);
  const gate = morningGate(audit);
  return (
    <div className="owner-mornings owner-audit">
      <h3 className="owner-overview__h">{ADVANCED_TITLES.audit}{audit.day ? ` — ${audit.day}` : ""}</h3>
      <div className="owner-health">
        <div className="owner-health__item"><strong>{audit.members.toLocaleString("en-US")}</strong><span>Mornings</span></div>
        <div className="owner-health__item"><strong>{audit.sent.toLocaleString("en-US")}</strong><span>Sent</span></div>
        {/* The one figure on this page that a beacon paid for. */}
        <div className="owner-health__item"><strong>{audit.delivered.toLocaleString("en-US")}</strong><span>Delivered</span></div>
        <div className="owner-health__item"><strong>{audit.gaps.length.toLocaleString("en-US")}</strong><span>Gaps</span></div>
      </div>

      {/* **THE GAP LIST IS PER MEMBER, WHICH IS WHAT SHE ASKED FOR.** Ranked worst-first, so the
          state that indicts the instrument (a send with no beacon row at all) is never buried under
          a page of phones that are merely off. */}
      {audit.gaps.length ? (
        <ul className="owner-delivery__members">
          {audit.gaps.map((g) => (
            <li key={`${g.who}:${g.tag}:${g.state}`}>
              <strong>{g.who}</strong>
              <span>{morningAuditState(g.state)?.label ?? g.state}</span>
              <em className="owner-delivery__cause">
                {morningAuditState(g.state)?.why ?? ""}
                {g.state === "skipped" && g.reason ? ` (${g.reason})` : ""}
                {g.state === "silent" && g.cause ? ` ${deliveryCause(g.cause).why} ${deliveryCause(g.cause).act}` : ""}
              </em>
            </li>
          ))}
        </ul>
      ) : null}

      {/* DT-5 — **THE GATE, PRINTED AS A VERDICT RATHER THAN LEFT AS A JUDGEMENT SOMEBODY MAKES.**
          Her order: the all-members open proceeds ONLY when the chain is green or named-and-healing
          for every rostered member, and the gaps are the work. It is written to SHUT, so a morning
          whose evidence failed to gather reads as not-yet rather than as clear. */}
      <p className={`owner-mornings__note owner-audit__gate owner-audit__gate--${gate.open ? "open" : "shut"}`} role="status">
        <strong>{gate.open ? "The all-members open may proceed." : "The all-members open waits."}</strong> {gate.reason}
      </p>

      {note ? <p className="owner-mornings__note" role="status">{note}</p> : null}
    </div>
  );
}

// DL-3.3 — **THE MORNING LEDGER, ON HER SCREEN.** Hookless on purpose, so the gate CALLS it in
// every state: the numbers here are the only place a morning that never fired is visible, and a
// strip that reads correctly on a good day and says nothing on a bad one is worse than no strip.
//
// **THE THREE FACTS ARE DRAWN APART** (`morningStrip`): sent, skipped, and STARTED AND NEVER
// FINISHED. And a day with no rows at all is the one state that carries a sentence, because it is
// the only one that means the job did not run rather than the job deciding something.
//
// `health: null` is a payload that has not arrived, which is R18's distinction and W5-2/R39's third
// state — it holds still rather than drawing a confident row of zeros.
export function OwnerMorningStrip({ health }: { health: MorningHealth | null }) {
  if (!health) return null;
  const strip = morningStrip(health);
  const note = morningNote(strip);
  return (
    <div className="owner-mornings">
      <h3 className="owner-overview__h">{ADVANCED_TITLES.mornings}{strip.day ? ` — ${strip.day}` : ""}</h3>
      {/* ONE PLAIN PHRASE UNDER EVERY NUMBER (her order): a count with no sentence is a count she has
          to interpret, and these three used to read Sent / Skipped / Unfinished with nothing else. */}
      <div className="owner-health">
        <div className="owner-health__item"><strong>{strip.sent.toLocaleString("en-US")}</strong><span>Sent</span><em>{advancedCaption("mornings.sent")}</em></div>
        <div className="owner-health__item"><strong>{strip.skipped.toLocaleString("en-US")}</strong><span>Skipped</span><em>{advancedCaption("mornings.skipped")}</em></div>
        <div className="owner-health__item"><strong>{strip.unfinished.toLocaleString("en-US")}</strong><span>Unfinished</span><em>{advancedCaption("mornings.unfinished")}</em></div>
      </div>
      {/* A TOTAL IS NOT A PATTERN. Ranked, so the reason that is happening most is the one she reads
          first, and every reason the ledger holds is printed rather than a top-one summary. */}
      {strip.reasons.length ? (
        <ul className="owner-mornings__reasons">
          {strip.reasons.map((r) => (
            <li key={r.reason}><strong>{r.n.toLocaleString("en-US")}</strong> <span>{r.reason}</span></li>
          ))}
        </ul>
      ) : null}
      {note ? <p className="owner-mornings__note" role="status">{note}</p> : null}
    </div>
  );
}

// D6-2 — THE RANGE CONTROL. Lifted out so the dashboard owns the state and every ranged tab reads
// the same one; this renders it and nothing more.
export function RangePicker({
  value, from, to, onChange,
}: {
  value: string; from: string; to: string;
  onChange: (next: { key: string; from?: string; to?: string }) => void;
}) {
  return (
    <div className="owner-range owner-daterange">
      {/* C7 — **ONE DROPDOWN, MOBILE AND DESKTOP, REPLACING THE STRETCHED BUTTON ROW.** Her order.
          A row of eight pill buttons is what she is pointing at: on a phone it wrapped to two rows
          and then scrolled sideways (D6-6), and adding her four new periods to it would have made
          that worse rather than better.

          **THE OPTIONS ARE THE LAW'S OWN ROSTER, WHOLE — Custom INCLUDED.** The retired control
          filtered Custom out and then hand-wrote a Custom button beside the loop, which is a second
          copy of one roster entry (R28/R29) and the exact shape that lets a control and the server
          offer different periods. Every option this dashboard has comes off `RANGES` now, so a
          ninth period she asks for joins by being declared.

          `data-range-picker` is the control's IDENTITY for a rendered proof (R41), and a walk
          selects by the option's own VALUE rather than by its words — ITEM A's lesson, which cost
          five walkthroughs four days of measuring nothing. */}
      <label className="owner-daterange__label" htmlFor="owner-range-select">Period</label>
      <select id="owner-range-select" className="owner-daterange__select" data-range-picker=""
        aria-label="Period" value={value}
        onChange={(e) => {
          const key = e.target.value;
          // Custom carries the two dates she already typed, so re-opening it does not blank them —
          // which is what the retired Custom button did and is the one behaviour that had to survive
          // the change of control.
          onChange(key === "custom" ? { key, from, to } : { key });
        }}>
        {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      {value === "custom" ? (
        <span className="owner-daterange__custom">
          <input type="date" value={from} aria-label="From"
            onChange={(e) => onChange({ key: "custom", from: e.target.value, to })} />
          <input type="date" value={to} aria-label="To"
            onChange={(e) => onChange({ key: "custom", from, to: e.target.value })} />
        </span>
      ) : null}
    </div>
  );
}
